import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import { useTableStoreContext } from '@/components/table/contexts/TableContext.tsx';
import { tablePreferencesStorage } from '@/components/table/preferences/tablePreferencesStorage.ts';
import {
  buildDefaultColumnPreferences,
  resolveColumnPreferences,
} from '@/components/table/preferences/columnPreferences.ts';
import { TablePreferences } from '@/components/table/preferences/tablePreferences.ts';

/**
 * Loads persisted column preferences for a table, reconciles them against the
 * backend table definition (resetting stale entries), and keeps any later
 * change persisted back to storage.
 */
const useTablePreferences = ({
  tableKey,
  hasSelect,
  hasOptions,
}: {
  tableKey: string;
  hasSelect: boolean;
  hasOptions: boolean;
}) => {
  const tableDefinition = useTableStoreContext((s) => s.tableDefinition);
  const applyColumnPreferences = useTableStoreContext(
    (s) => s.applyColumnPreferences,
  );
  const columnOrder = useTableStoreContext((s) => s.columnOrder);
  const columnVisibility = useTableStoreContext((s) => s.columnVisibility);
  const columnSizing = useTableStoreContext((s) => s.columnSizing);
  const ready = useTableStoreContext((s) => s.columnPreferencesReady);

  const initStartedRef = useRef(false);

  // One-time init once the backend table definition is available.
  useEffect(() => {
    if (initStartedRef.current) return;
    if (!tableDefinition.fields || tableDefinition.fields.length === 0) return;
    initStartedRef.current = true;

    let cancelled = false;
    const system = { hasSelect, hasOptions };
    const defaults = buildDefaultColumnPreferences(tableDefinition, system);

    tablePreferencesStorage
      .load(tableKey)
      .then((persisted) => {
        if (cancelled) return;
        const { preferences, wasReset } = resolveColumnPreferences(
          persisted,
          tableDefinition,
          system,
        );
        if (wasReset) {
          void tablePreferencesStorage.remove(tableKey);
        }
        applyColumnPreferences(preferences, defaults);
      })
      .catch(() => {
        if (!cancelled) applyColumnPreferences(defaults, defaults);
      });

    return () => {
      cancelled = true;
    };
  }, [tableDefinition, tableKey, hasSelect, hasOptions, applyColumnPreferences]);

  const debouncedSave = useMemo(
    () =>
      debounce((key: string, preferences: TablePreferences) => {
        void tablePreferencesStorage.save(key, preferences);
      }, 400),
    [],
  );

  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  // Persist any change made after the initial load.
  useEffect(() => {
    if (!ready) return;
    debouncedSave(tableKey, { columnOrder, columnVisibility, columnSizing });
  }, [
    ready,
    tableKey,
    columnOrder,
    columnVisibility,
    columnSizing,
    debouncedSave,
  ]);
};

export default useTablePreferences;
